/**
 * QuizSession Component
 *
 * Typeform-style quiz interface for learners.
 * One question at a time with smooth transitions.
 *
 * Features:
 * - Progress bar showing current position
 * - Keyboard shortcuts (1-4 for options, Enter for next, Backspace for prev)
 * - Auto-advance after selection (400ms delay)
 * - Review screen before submission
 * - Mobile-friendly design
 *
 * F-QUIZ-005: Learner Quiz UI
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { QuizProgress } from './QuizProgress';
import { QuizQuestion } from './QuizQuestion';
import { QuizReview } from './QuizReview';
import type { QuizQuestion as QuizQuestionType, SubmitQuizResponse } from '../lib/types';
import { ApiClient } from '../lib/api';
import './QuizSession.css';

type QuizView = 'question' | 'review';

export interface QuizSessionProps {
  questions: QuizQuestionType[];
  assessmentId: string;
  client: ApiClient;
  onComplete: (result: SubmitQuizResponse) => void;
  onError: (error: Error) => void;
  disabled?: boolean;
}

const AUTO_ADVANCE_DELAY = 400; // ms

export function QuizSession({
  questions,
  assessmentId,
  client,
  onComplete,
  onError,
  disabled = false,
}: QuizSessionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, number>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [view, setView] = useState<QuizView>('question');
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const [isAnimating, setIsAnimating] = useState(false);

  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memoize current question
  const currentQuestion = useMemo(
    () => questions[currentIndex],
    [questions, currentIndex]
  );

  // Check if current question is answered
  const currentAnswer = currentQuestion ? answers.get(currentQuestion.id) : null;

  // Navigation helpers
  const canGoNext = currentIndex < questions.length - 1;
  const canGoPrev = currentIndex > 0;
  const isLastQuestion = currentIndex === questions.length - 1;

  // Clear auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  // Handle option selection with auto-advance
  const handleSelectOption = useCallback(
    (optionIndex: number) => {
      if (!currentQuestion || disabled || isSubmitting) return;

      // Update answer
      setAnswers((prev) => {
        const next = new Map(prev);
        next.set(currentQuestion.id, optionIndex);
        return next;
      });

      // Clear any pending auto-advance
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }

      // Auto-advance to next question (or review) after delay
      autoAdvanceTimerRef.current = setTimeout(() => {
        if (isLastQuestion) {
          setView('review');
        } else {
          setSlideDirection('left');
          setIsAnimating(true);
          setTimeout(() => {
            setCurrentIndex((prev) => prev + 1);
            setIsAnimating(false);
          }, 300);
        }
      }, AUTO_ADVANCE_DELAY);
    },
    [currentQuestion, disabled, isSubmitting, isLastQuestion]
  );

  // Navigate to next question
  const goToNext = useCallback(() => {
    if (!canGoNext || isAnimating) return;

    // Clear auto-advance timer
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
    }

    setSlideDirection('left');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
      setIsAnimating(false);
    }, 300);
  }, [canGoNext, isAnimating]);

  // Navigate to previous question
  const goToPrev = useCallback(() => {
    if (!canGoPrev || isAnimating) return;

    // Clear auto-advance timer
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
    }

    setSlideDirection('right');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => prev - 1);
      setIsAnimating(false);
    }, 300);
  }, [canGoPrev, isAnimating]);

  // Go to specific question from review
  const goToQuestion = useCallback((index: number) => {
    if (index >= 0 && index < questions.length) {
      setCurrentIndex(index);
      setView('question');
    }
  }, [questions.length]);

  // Show review screen
  const showReview = useCallback(() => {
    setView('review');
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (view !== 'question' || disabled || isSubmitting) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && currentAnswer !== null) {
        event.preventDefault();
        if (isLastQuestion) {
          showReview();
        } else {
          goToNext();
        }
      } else if (event.key === 'Backspace' && canGoPrev) {
        event.preventDefault();
        goToPrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [view, disabled, isSubmitting, currentAnswer, isLastQuestion, canGoPrev, goToNext, goToPrev, showReview]);

  // Submit quiz
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    // Convert answers Map to array format
    const answersArray = questions
      .map((q) => {
        const selected = answers.get(q.id);
        if (selected === undefined) return null;
        return {
          questionId: q.id,
          selectedAnswerIndex: selected,
        };
      })
      .filter((a): a is { questionId: string; selectedAnswerIndex: number } => a !== null);

    try {
      const result = await client.submitQuiz(assessmentId, answersArray);

      if (result.response.error) {
        throw new Error(result.response.error.message || 'Submission failed');
      }

      if (!result.response.data) {
        throw new Error('No response data received');
      }

      onComplete(result.response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed';
      setSubmitError(message);
      setIsSubmitting(false);
      onError(err instanceof Error ? err : new Error(message));
    }
  }, [isSubmitting, questions, answers, client, assessmentId, onComplete, onError]);

  // Render question view
  if (view === 'question' && currentQuestion) {
    return (
      <div className="quiz-session">
        <QuizProgress current={currentIndex} total={questions.length} />

        <div
          className={`quiz-question-container ${isAnimating ? `slide-${slideDirection}` : ''}`}
        >
          <QuizQuestion
            question={currentQuestion}
            selectedIndex={currentAnswer ?? null}
            onSelect={handleSelectOption}
            disabled={disabled || isSubmitting}
          />
        </div>

        <div className="quiz-navigation">
          <button
            className="btn btn-secondary quiz-nav-btn"
            onClick={goToPrev}
            disabled={!canGoPrev || isAnimating || disabled}
          >
            Previous
          </button>

          {isLastQuestion ? (
            <button
              className="btn btn-primary quiz-nav-btn"
              onClick={showReview}
              disabled={currentAnswer === null || isAnimating || disabled}
            >
              Review Answers
            </button>
          ) : (
            <button
              className="btn btn-primary quiz-nav-btn"
              onClick={goToNext}
              disabled={currentAnswer === null || isAnimating || disabled}
            >
              Next
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render review view
  if (view === 'review') {
    return (
      <div className="quiz-session">
        <QuizReview
          questions={questions}
          answers={answers}
          onGoToQuestion={goToQuestion}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />

        {submitError && (
          <div className="error-box mt-4">
            <p>{submitError}</p>
            <button
              className="btn btn-secondary mt-4"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  // Fallback (shouldn't happen)
  return null;
}
